import { z } from 'zod';

export enum CUSTOMER_TYPE_VALUES {
  PAY_MONTHLY = 'M',
  PAY_AS_GO = 'G',
  BUSINESS = 'B',
  NOT_CUSTOMER = 'N',
}

export enum PRODUCT_TYPE_VALUES {
  PHONE_PRODUCTS = 'pho',
  TABLET_PRODUCTS = 'tab',
  ACCESSORY_PRODUCTS = 'acc',
  FIVEG_PRODUCTS = '5gn',
  PERK_PRODUCTS = 'per',
  NEW_PRODUCTS = 'npl',
}

export enum BRAND_TYPE_VALUES {
  APPLE_BRAND = 'app',
  SAMSUNG_BRAND = 'sam',
  GOOGLE_BRAND = 'goo',
  SONY_BRAND = 'son',
  OTHER_BRAND = 'omf',
}

export enum FORM_ERROR_MESSAGES {
  FIRST_NAME_REQUIRED = 'First name is required.',
  FIRST_NAME_EXCEEDED = 'First name has exceeded character limit.',
  LAST_NAME_REQUIRED = 'Last name is required.',
  LAST_NAME_EXCEEDED = 'Last name has exceeded character limit.',
  EMAIL_REQUIRED = 'Email is required.',
  EMAIL_EXCEEDED = 'Email has exceeded character limit.',
  EMAIL_INVALID = 'Invalid email.',
  CONTACT_NUMBER_INVALID = 'Invalid contact number.',
  CONTACT_NUMBER_REQUIRED = 'Mobile number is required.',
  CUSTOMER_TYPE_REQUIRED = 'Your tariff is required.',
}

export const FORM_FIELD_ORDER: (keyof GetReleaseUpdatesForm)[] = [
  'firstName',
  'lastName',
  'emailAddr',
  'contactNo',
  'customerType',
  'brands',
  'productsAndServices',
];

export const getReleaseUpdatesSchema = z.object({
  firstName: z
    .string()
    .transform((val) => val.trim())
    .refine((val) => val.length >= 2, FORM_ERROR_MESSAGES.FIRST_NAME_REQUIRED)
    .refine((val) => val.length <= 50, FORM_ERROR_MESSAGES.FIRST_NAME_EXCEEDED),
  lastName: z
    .string()
    .transform((val) => val.trim())
    .refine((val) => val.length >= 2, FORM_ERROR_MESSAGES.LAST_NAME_REQUIRED)
    .refine((val) => val.length <= 50, FORM_ERROR_MESSAGES.LAST_NAME_EXCEEDED),
  emailAddr: z
    .string()
    .min(1, { message: FORM_ERROR_MESSAGES.EMAIL_REQUIRED })
    .max(120, FORM_ERROR_MESSAGES.EMAIL_EXCEEDED)
    .refine((val) => !val || z.string().email().safeParse(val).success, {
      message: FORM_ERROR_MESSAGES.EMAIL_INVALID,
    }),
  contactNo: z
    .string()
    .min(1, FORM_ERROR_MESSAGES.CONTACT_NUMBER_REQUIRED)
    .transform((val) => val.replace(/[^0-9+]/g, '')) // Remove all non-numeric characters except +
    .refine(
      (val) => {
        // Allow:
        // - Standard UK mobile: 07xxxxxxxxx
        // - International format: +447xxxxxxxxx or 00447xxxxxxxxx
        // - With or without spaces/dashes
        return /^(\+44|0044|0)7\d{9}$/.test(val);
      },
      { message: FORM_ERROR_MESSAGES.CONTACT_NUMBER_INVALID }
    ),
  customerType: z
    .nativeEnum(CUSTOMER_TYPE_VALUES)
    .nullable()
    .refine(Boolean, FORM_ERROR_MESSAGES.CUSTOMER_TYPE_REQUIRED),
  brands: z.preprocess(
    (val) => {
      if (val === false) return [];
      return val;
    },
    z.array(z.nativeEnum(BRAND_TYPE_VALUES))
  ),
  productsAndServices: z.preprocess(
    (val) => {
      if (val === false) return [];
      return val;
    },
    z.array(z.nativeEnum(PRODUCT_TYPE_VALUES))
  ),
});

export type GetReleaseUpdatesForm = z.infer<typeof getReleaseUpdatesSchema>;
